import { ElementRef, EventEmitter, NgZone, SimpleChanges, } from '@angular/core';
import uuid from 'uuid-random';
import { allSeriesExceedDataLimit, errorMessage, getStartedMessage, noDataMessage, } from '../../constants/messages.constants';
import { SCATTER } from 'NewAngular/constants/core.constants';
import { ScatterPlotChartType, ScatterPlotStatus } from 'ChartApp/ts/enums/scatter-plot-common.enum';
import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';
import { YChartsScatterPlotService } from 'ChartApp/ts/services/ycharts-scatter-plot-service/ycharts-scatter-plot.service';
import { ChartZoomControlsComponent } from 'ChartApp/ts/features/charts-shared/components/chart-zoom-controls/chart-zoom-controls.component';
import { observeContainerResize } from 'ChartApp/ts/utils/chart-container-resize';
import * as i0 from "@angular/core";
import * as i1 from "ChartApp/ts/features/charts-shared/services/chart-zoom.service";
import * as i2 from "@angular/common";
import * as i3 from "../../../../../../../../../ycharts/static/ts/features/components/loaders/loading-symbol/loading-symbol.component";
import * as i4 from "ChartApp/ts/features/charts-shared/components/chart-zoom-controls/chart-zoom-controls.component";
const _c0 = () => ({ height: "100%" });
function ScatterPlotComponent_ycn_loading_symbol_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-loading-symbol", 7);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("loaderMessage", ctx_r0.loaderMessage);
} }
/**
 * Scatter Plot Component
 *
 * Inputs:
 * scatterPlotInfo: The response returned from the backend ScatterPlotDataView.
 * status: The status of the scatterPlotInfo being fetched.
 * errorMessage: If there is an error, this is error message from the backend to be displayed to the user.
 * loaderMessage: Message to show while loading the scatter plot
 * ychartsScatterPlotService: An instance of the YChartsScatterPlotService used across the scatter plot app.
 */
export class ScatterPlotComponent {
    constructor(ngZone, elementRef, chartZoomService) {
        this.ngZone = ngZone;
        this.elementRef = elementRef;
        this.chartZoomService = chartZoomService;
        this.loaderMessage = 'Loading ...';
        this.hideZoomInstructions = false;
        this.selectedPoint = null;
        this.customClick = new EventEmitter();
        this.resizeObserver = null;
        this.message = getStartedMessage;
        // Other
        this.mouseOverChart = false;
        this.mouseCoords = null;
    }
    ngOnInit() {
        this.chartElemId = `hChartScatterPlot-${uuid()}`;
        this.elementRef.nativeElement.addEventListener('mousedown', (event) => this.setMouseDownCoords(event));
        this.elementRef.nativeElement.addEventListener('mouseup', (event) => this.handleClickOrDrag(event));
    }
    ngOnChanges(changes) {
        if (changes?.status) {
            this.handleStatus(changes.status.currentValue);
        }
    }
    handleStatus(status) {
        switch (status) {
            case ScatterPlotStatus.allSeriesExceedDataLimit:
                this.message = allSeriesExceedDataLimit;
                break;
            case ScatterPlotStatus.getStarted: {
                this.message = getStartedMessage;
                break;
            }
            case ScatterPlotStatus.noData: {
                const potentialErrorMessage = this.ychartsScatterPlotService.getSpecificErrorMessage(this.scatterPlotInfo.data.series);
                this.message = potentialErrorMessage || noDataMessage;
                this.chartZoomService.clearChart();
                break;
            }
            case ScatterPlotStatus.ok: {
                this.message = '';
                setTimeout(() => {
                    this.renderChart();
                });
                break;
            }
            default: {
                this.message = `${errorMessage}<br>${this.errorMessage}.`;
                break;
            }
        }
    }
    /**
     * Configure the chart and add the chart html to the DOM.
     * */
    renderChart() {
        // If the chart elem is not in the DOM, do not attempt to render the chart
        if (!document.getElementById(this.chartElemId) || !this.scatterPlotInfo || !this.ychartsScatterPlotService)
            return;
        // Merge user selected chart options with default options
        this.options = this.ychartsScatterPlotService.createChartOptions(this.scatterPlotInfo, this.chartElemId, ScatterPlotChartType.interactive);
        // Create a new Highchart and attach it to this.chartElemId
        if (this.chart)
            this.chart.destroy();
        this.options = this.chartZoomService.addZoomOptions(this.options);
        this.chart = this.ychartsScatterPlotService.createChartFromOptions(this.chartElemId, this.options);
        this.chartZoomService.setChart(this.chart);
        if (this.scatterPlotInfo.params.axisExtremes) {
            this.chartZoomService.zoomIn(this.scatterPlotInfo.params.axisExtremes);
        }
        if (this.selectedPoint) {
            const selectedSeries = this.chart.series.find(({ userOptions }) => userOptions.id === this.selectedPoint?.seriesId && userOptions.type === SCATTER);
            selectedSeries.data[this.selectedPoint.pointIndex].select();
        }
        this.setupResizeObserver();
    }
    /**
     * Set up a ResizeObserver for the host element, so we can dynamically resize the chart
     * when the size of the host element changes.
     * @private
     */
    setupResizeObserver() {
        const chartEl = document.getElementById(this.chartElemId);
        if (this.resizeObserver || !chartEl) {
            return;
        }
        this.resizeObserver = observeContainerResize(chartEl, (width, height) => this.chart?.setSize(width, height, false));
    }
    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
    setMouseDownCoords(event) {
        this.mouseCoords = {
            x: event.clientX,
            y: event.clientY,
        };
    }
    handleClickOrDrag(event) {
        if (!this.zoomControls?.enablePanning && this.mouseCoords) {
            const { x, y } = this.mouseCoords;
            const mouseUpX = event.clientX;
            const mouseUpY = event.clientY;
            const isClick = this.areCoordsInClickRange(x, mouseUpX) && this.areCoordsInClickRange(y, mouseUpY);
            if (isClick) {
                const clickTarget = event.target;
                const isValidClick = ['highcharts-plot-background', 'highcharts-point'].some((className) => clickTarget.classList.value.includes(className));
                if (isValidClick) {
                    this.customClick.emit();
                }
            }
        }
    }
    areCoordsInClickRange(mouseDown, mouseUp) {
        return Boolean(Math.abs(mouseDown - mouseUp) < 3);
    }
}
ScatterPlotComponent.ɵfac = function ScatterPlotComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ScatterPlotComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.ChartZoomService)); };
ScatterPlotComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ScatterPlotComponent, selectors: [["ycn-scatter-plot"]], viewQuery: function ScatterPlotComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(ChartZoomControlsComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.zoomControls = _t.first);
    } }, inputs: { scatterPlotInfo: "scatterPlotInfo", status: "status", errorMessage: "errorMessage", loaderMessage: "loaderMessage", ychartsScatterPlotService: "ychartsScatterPlotService", hideZoomInstructions: "hideZoomInstructions", selectedPoint: "selectedPoint" }, outputs: { customClick: "customClick" }, standalone: false, features: [i0.ɵɵNgOnChangesFeature], decls: 7, vars: 12, consts: [[3, "loaderMessage", 4, "ngIf"], [3, "hidden", "top", "mouseOverChart", "hideZoomInstructions"], ["id", "chartGetStarted", 1, "row", "row-full-height", 3, "hidden"], [1, "col-12"], [1, "get-started"], [3, "innerHTML"], [1, "flex-auto", "full-height", 2, "height", "0", "overflow", "hidden", 3, "mouseenter", "mouseleave", "id", "hidden"], [3, "loaderMessage"]], template: function ScatterPlotComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, ScatterPlotComponent_ycn_loading_symbol_0_Template, 1, 1, "ycn-loading-symbol", 0);
        i0.ɵɵelement(1, "ycn-chart-zoom-controls", 1);
        i0.ɵɵelementStart(2, "div", 2)(3, "div", 3)(4, "div", 4);
        i0.ɵɵelement(5, "span", 5);
        i0.ɵɵelementEnd()()();
        i0.ɵɵelementStart(6, "div", 6);
        i0.ɵɵlistener("mouseenter", function ScatterPlotComponent_Template_div_mouseenter_6_listener() { return ctx.mouseOverChart = true; })("mouseleave", function ScatterPlotComponent_Template_div_mouseleave_6_listener() { return ctx.mouseOverChart = false; });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.status === "loading");
        i0.ɵɵadvance();
        i0.ɵɵproperty("hidden", ctx.status === "loading")("top", 14)("mouseOverChart", ctx.mouseOverChart)("hideZoomInstructions", ctx.hideZoomInstructions);
        i0.ɵɵadvance();
        i0.ɵɵstyleMap(i0.ɵɵpureFunction0(11, _c0));
        i0.ɵɵproperty("hidden", ctx.status !== "getStarted" && ctx.status !== "noData" && ctx.status !== "allSeriesExceedDataLimit" && ctx.status !== "error");
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("innerHTML", ctx.message, i0.ɵɵsanitizeHtml);
        i0.ɵɵadvance();
        i0.ɵɵproperty("id", ctx.chartElemId)("hidden", ctx.status !== "ok");
    } }, dependencies: [i2.NgIf, i3.LoadingSymbolComponent, i4.ChartZoomControlsComponent], encapsulation: 2 });
